import { useState, useEffect } from 'react';
import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';
import type { GraphQLTaggedNode } from 'relay-runtime';

import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import {
	useQueryInterop,
	type UseQueryInteropSupportedResult,
} from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

type FeedDropdownStorageConfig<TValue> = {
	defaultValue: TValue;
	sessionStorageKey: string;
	skipQuery?: boolean;
	interopApolloAggClient?: boolean;
};

type FeedDropdownQueryConfig<TVariables, TValue> = {
	query: GraphQLTaggedNode;
	variables: (value: TValue) => Omit<TVariables, 'first'>;
};

export function useFeedDropdownStorage<TData, TVariables extends Record<string, any>, TValue>(
	config: FeedDropdownStorageConfig<TValue>,
	queryConfig: FeedDropdownQueryConfig<TVariables, TValue>,
): {
	value: TValue;
	setValue: (newValue: TValue) => void;
	query: UseQueryInteropSupportedResult<TData, TVariables>;
} {
	const { defaultValue, sessionStorageKey, skipQuery, interopApolloAggClient } = config;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = process.env.REACT_SSR || window.__SSR_RENDERED__;

	// Initialize state with session storage or default
	const [value, setValue] = useState((): TValue => {
		if (!isSSR) {
			const sessionValue = sessionStorage.getItem(sessionStorageKey);
			if (
				sessionValue &&
				sessionValue !== defaultValue &&
				expValEquals('confluence_feeds_sorting_retention_experiment', 'cohort', 'test')
			) {
				return sessionValue as TValue;
			}
		}
		return defaultValue;
	});

	const isFollowingFeed = defaultValue === 'relevant';

	const popularFeedInitialPageSize = fg('confluence_ai_feeds_home_banner')
		? 10
		: expVal('confluence_home_feed_initial_page_size', 'popular_spa', 5);

	const initialPageSize = isFollowingFeed
		? expVal('confluence_home_feed_initial_page_size', 'following_spa', 5)
		: popularFeedInitialPageSize;

	const query = useQueryInterop<TData, TVariables>(queryConfig.query, {
		variables: {
			first: initialPageSize,
			...queryConfig.variables(value),
		} as unknown as TVariables,
		context: { single: true },
		errorPolicy: 'all',
		notifyOnNetworkStatusChange: true,
		skip: skipQuery,
		interopApolloAggClient,
	});

	useEffect(() => {
		// If refetching, reset to default and update session storage, else set the value
		const valueToSet =
			query.networkStatus === NetworkStatus.loading || query.networkStatus === NetworkStatus.refetch
				? defaultValue
				: value;

		setValue(valueToSet);
		sessionStorage.setItem(sessionStorageKey, valueToSet as string);
	}, [query.networkStatus, value, defaultValue, sessionStorageKey]);

	return { value, setValue, query };
}
